// Proje detay sayfası
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  FiEdit, 
  FiPlus, 
  FiCalendar, 
  FiFileText, 
  FiUsers,
  FiClock,
  FiMapPin,
  FiTag,
  FiTrash2,
  FiSettings,
  FiX,
  FiInfo,
  FiPackage,
  FiUser,
  FiTrendingUp
} from 'react-icons/fi';
import { AiOutlineUpload } from 'react-icons/ai';
import { CgMenuGridO } from 'react-icons/cg';
import { IoChevronBackOutline } from 'react-icons/io5';
import { projectService } from '../../services/projectService';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import ProjectMeetingModal from '../../components/Projects/ProjectMeetingModal';
import ProjectNoteModal from '../../components/Projects/ProjectNoteModal';
import DeleteConfirmModal from '../../components/Common/DeleteConfirmModal';
import ProjectModal from '../../components/Projects/ProjectModal';
import toast from 'react-hot-toast';
import CurrencyInput from 'react-currency-input-field';
import { formatDateTime, formatDateOnly, formatTimeOnly } from '../../utils/dateUtils';

const ProjectDetail = () => {
  const { id } = useParams();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editingOffer, setEditingOffer] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusDescription, setStatusDescription] = useState('');
  // Backend origin (dosya linkleri için tam URL)
  const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/$/, '').replace(/\/api$/, '');

  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadMessageType, setUploadMessageType] = useState(null); // 'success' | 'error'
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState({ type: null, id: null, title: '' });
  const [offerStatus, setOfferStatus] = useState('Hazırlandı');
  const [offerStatusModal, setOfferStatusModal] = useState({ visible: false, offerId: null, status: 'Hazırlandı' });
  const [offerDescription, setOfferDescription] = useState('');
  const [offerTotal, setOfferTotal] = useState('');
  const [offerProfitability, setOfferProfitability] = useState('');
  const [offerCurrency, setOfferCurrency] = useState('TL');
  const [offerCreatedAt, setOfferCreatedAt] = useState(new Date().toISOString().slice(0, 16));
  const [offerDate, setOfferDate] = useState(new Date().toISOString().slice(0, 10));
  const [offerTime, setOfferTime] = useState(new Date().toISOString().slice(11, 16));
  const [selectedOfferFile, setSelectedOfferFile] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [showDeleteFileModal, setShowDeleteFileModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  // Dosya silme fonksiyonu
  const handleDeleteFile = (offer) => {
    setFileToDelete(offer);
    setShowDeleteFileModal(true);
  };

  const confirmDeleteFile = async () => {
    if (!fileToDelete) return;
    
    try {
      await projectService.deleteProjectOfferFile(fileToDelete.id);
      toast.success('Dosya silindi');
      queryClient.invalidateQueries(['project', id]);
      setShowDeleteFileModal(false);
      setFileToDelete(null);
    } catch (error) {
      toast.error('Dosya silinemedi');
    }
  };

  // Dosya yükleme handler'ı
  const handleFiles = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const file = fileList[0];
    try {
      setUploading(true);
      await projectService.uploadProjectFile(id, file, uploadDescription);
      // Başarı popup
      setUploadMessage('Dosya başarıyla yüklendi.');
      setUploadMessageType('success');
      setShowInfoModal(true);
      setUploadDescription('');
      queryClient.invalidateQueries(['project', id]);
    } catch (error) {
      const msg = error.response?.data?.message || 'Dosya yüklenemedi';
      toast.error(msg);
      setUploadMessage(msg);
      setUploadMessageType('error');
    } finally {
      setUploading(false);
    }
  }, [id, uploadDescription, queryClient]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  }, [handleFiles]);

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  // Upload bilgi popup'ı artık otomatik kapanmıyor; yalnızca kullanıcı kapatır

  // Genel silme onayı işlemi
  const handleConfirmDelete = async () => {
    if (!confirmData?.type || !confirmData?.id) return;
    try {
      if (confirmData.type === 'meeting') {
        deleteMeetingMutation.mutate(confirmData.id);
      } else if (confirmData.type === 'note') {
        deleteNoteMutation.mutate(confirmData.id);
      } else if (confirmData.type === 'file') {
        await projectService.deleteProjectFile(confirmData.id);
        toast.success('Dosya silindi');
        queryClient.invalidateQueries(['project', id]);
      } else if (confirmData.type === 'offer') {
        await projectService.deleteOffer(confirmData.id);
        toast.success('Teklif silindi');
        queryClient.invalidateQueries(['project', id]);
      }
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    } finally {
      setShowConfirmModal(false);
      setConfirmData({ type: null, id: null, title: '' });
    }
  };

  const handleUpdateOffer = async () => {
    if (!editingOffer) return;
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('offer_file_description', offerDescription);
      formData.append('offer_total', offerTotal);
      formData.append('profitability', offerProfitability);
      formData.append('currency', offerCurrency);
      formData.append('offer_status', offerStatus);
      
      // Tarih ve saat birleştirme
      const combinedDateTime = offerDate && offerTime ? `${offerDate}T${offerTime}:00` : new Date().toISOString();
      formData.append('created_at', combinedDateTime);
      
      if (selectedOfferFile) {
        formData.append('offer_file', selectedOfferFile);
      }

      await projectService.updateOffer(editingOffer.id, formData);
      toast.success('Teklif güncellendi');
      setShowOfferModal(false);
      setEditingOffer(null);
      setOfferDescription('');
      setOfferTotal('');
      setOfferProfitability('');
      setOfferCurrency('TRY');
      setOfferDate('');
      setOfferTime('');
      setSelectedOfferFile(null);
      queryClient.invalidateQueries(['project', id]);
    } catch (error) {
      const msg = error.response?.data?.message || 'Teklif güncellenemedi';
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  // Fırsat değerini rakam + metin karşılığına çeviren fonksiyon
  const getOpportunityText = (value) => {
    switch (value) {
      case '0': return '0 (Henüz belli değil)';
      case '1': return '1 (Çok Kötü)';
      case '2': return '2 (Kötü)';
      case '3': return '3 (Orta)';
      case '4': return '4 (İyi)';
      case '5': return '5 (Çok İyi)';
      default: return value;
    }
  };

  // Teklif durumuna göre rozet sınıfları
  const getOfferStatusClass = (status) => {
    const base = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    switch ((status || '').toLowerCase()) {
      case 'hazırlandı':
        return `${base} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
      case 'gönderildi':
        return `${base} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'müşteri onay sürecinde':
        return `${base} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'müşteri revize istedi':
        return `${base} bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200`;
      case 'teklif kabul edilmedi':
        return `${base} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      case 'teklif onaylandı':
        return `${base} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'revize gönderilecek':
        return `${base} bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200`;
      default:
        return `${base} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
    }
  };

  // Menü dışına tıklandığında menüyü kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showActionMenu && !event.target.closest('.action-menu')) {
        setShowActionMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActionMenu]);

  // Toplantı durumu güncelleme mutation'ı
  const updateMeetingStatusMutation = useMutation(
    (data) => projectService.updateMeetingStatus(data.meetingId, data.status, data.description),
    {
      onSuccess: () => {
        toast.success('Toplantı durumu güncellendi');
        queryClient.invalidateQueries(['project', id]);
        setShowStatusModal(false);
        setSelectedMeeting(null);
        setNewStatus('');
        setStatusDescription('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Durum güncellenemedi');
      }
    }
  );

  // Toplantı silme mutation'ı
  const deleteMeetingMutation = useMutation(
    (meetingId) => projectService.deleteMeeting(meetingId),
    {
      onSuccess: () => {
        toast.success('Toplantı silindi');
        queryClient.invalidateQueries(['project', id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Toplantı silinemedi');
      }
    }
  );

  // Not silme mutation'ı
  const deleteNoteMutation = useMutation(
    (noteId) => projectService.deleteNote(noteId),
    {
      onSuccess: () => {
        toast.success('Not silindi');
        queryClient.invalidateQueries(['project', id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Not silinemedi');
      }
    }
  );

  // Proje düzenle
  const handleEditProject = () => {
    setEditingProject(project);
    setShowProjectModal(true);
  };

  // Durum değiştir
  const handleStatusChange = (meeting) => {
    setSelectedMeeting(meeting);
    setNewStatus(meeting.meeting_status);
    setStatusDescription(meeting.meeting_status_description || '');
    setShowStatusModal(true);
  };

  // Durum güncelle
  const handleUpdateStatus = () => {
    if (selectedMeeting && newStatus) {
      // İptal veya Ertelendi durumlarında açıklama zorunlu
      if ((newStatus === 'İptal' || newStatus === 'Ertelendi') && !statusDescription.trim()) {
        toast.error('İptal veya Ertelendi durumlarında açıklama girilmesi zorunludur');
        return;
      }
      
      // Planlandı veya Tamamlandı durumlarında açıklama alanını temizle
      let description = statusDescription;
      if (newStatus === 'Planlandı' || newStatus === 'Tamamlandı') {
        description = '';
      }
      
      updateMeetingStatusMutation.mutate({
        meetingId: selectedMeeting.id,
        status: newStatus,
        description: description
      });
    }
  };

  // Toplantı sil
  const handleDeleteMeeting = (meetingId) => {
    setConfirmData({ type: 'meeting', id: meetingId, title: 'Bu toplantıyı silmek istiyor musunuz?' });
    setShowConfirmModal(true);
  };

  // Not sil
  const handleDeleteNote = (noteId) => {
    setConfirmData({ type: 'note', id: noteId, title: 'Bu notu silmek istiyor musunuz?' });
    setShowConfirmModal(true);
  };

  // Proje detaylarını al
  const { data: projectData, isLoading } = useQuery(
    ['project', id],
    () => projectService.getProject(id),
    { enabled: !!id }
  );

  if (isLoading) {
    return <LoadingSpinner text="Proje bilgileri yükleniyor..." fullScreen={false} />;
  }

  const project = projectData?.project;
  const meetings = projectData?.meetings || [];
  const notes = projectData?.notes || [];
  const files = projectData?.files || [];
  const offers = projectData?.offers || [];

  if (!project) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">Proje bulunamadı</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sayfa başlığı */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => window.history.back()}
            className="flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            title="Geri"
          >
            <IoChevronBackOutline size={24} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {project.project_name}
          </h1>
        </div>
       
        
        {/* Guest kullanıcılar için action menüyü gizle */}
        {/* User kullanıcılar sadece kendi projelerini düzenleyebilir */}
        {currentUser?.user_type !== 'guest' && 
         !(currentUser?.user_type === 'user' && project.assigned_to !== currentUser?.id) && (
          <div className="relative action-menu">
            <button 
              onClick={() => setShowActionMenu((v) => !v)}
              className="btn-primary flex items-center"
            >
              <CgMenuGridO className="mr-2" size={16} />
              İşlemler
            </button>
          {showActionMenu && (
            <div className="absolute top-full right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-xl z-50 min-w-48">
              <div className="py-1">
                <button onClick={() => { handleEditProject(); setShowActionMenu(false); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                  <FiEdit className="mr-3" size={16} /> Düzenle
                </button>
                <button onClick={() => { setEditingMeeting(null); setShowMeetingModal(true); setShowActionMenu(false); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                  <FiCalendar className="mr-3" size={16} /> Yeni Toplantı
                </button>
                <button onClick={() => { setEditingNote(null); setShowNoteModal(true); setShowActionMenu(false); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                  <FiFileText className="mr-3" size={16} /> Yeni Not
                </button>
                <button onClick={() => { setShowOfferModal(true); setShowActionMenu(false); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                  <FiTrendingUp className="mr-3" size={16} /> Yeni Teklif
                </button>
                <button onClick={() => { /* Dosya yükleme modalı bağlanırsa aç */ setShowActionMenu(false); }} className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                  <FiPlus className="mr-3" size={16} /> Dosya Yükle
                </button>
              </div>
            </div>
          )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sol kolon - Proje bilgileri */}
        <div className="lg:col-span-1 space-y-6">
          {/* Proje kartı */}
          <div className="card p-0 overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Proje Bilgileri</h3>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Proje Adı */}
              <div className="flex items-start space-x-3">
                <FiFileText className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Proje Adı</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{project.project_name}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FiTag className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Durum</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    project.project_status === 'Aktif' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    (project.project_status || '').includes('Tamamlandı') ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {project.project_status}
                  </span>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FiUsers className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Müşteri
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {project.customer_name}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FiTrendingUp className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Fırsat
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {getOpportunityText(project.project_opportunity)}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FiFileText className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Açıklama</p>
                  <p className={`text-sm ${project.project_description ? 'text-gray-500 dark:text-gray-400' : 'text-red-500 dark:text-red-400'}`}>
                    {project.project_description || 'Henüz açıklama girilmemiş'}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FiPackage className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Ürünler</p>
                  <p className={`text-sm ${project.project_products ? 'text-gray-500 dark:text-gray-400' : 'text-red-500 dark:text-red-400'}`}>
                    {project.project_products || 'Henüz ürün bilgisi girilmemiş'}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FiUser className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Atanan Kullanıcı</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{project.assigned_to_name || '-'}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <FiCalendar className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Oluşturulma Tarihi</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{new Date(project.created_at).toLocaleDateString('tr-TR')}</p>
                </div>
              </div>

              {project.close_date && (
                <div className="flex items-start space-x-3">
                  <FiCalendar className="h-5 w-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Kapanma Tarihi</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{new Date(project.close_date).toLocaleDateString('tr-TR')}</p>
                  </div>
                </div>
              )}

              <div className="flex items-start space-x-3">
                <FiUsers className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Oluşturan</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{project.created_by || '-'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* İstatistikler */}
          <div className="card p-0 overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">İstatistikler</h3>
            </div>
            <div className="p-6">
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Toplam Toplantı
                </span>
                <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200 flex items-center justify-center shadow-md">
                  <span className="text-sm font-bold">{meetings.length}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Toplam Not
                </span>
                <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-200 flex items-center justify-center shadow-md">
                  <span className="text-sm font-bold">{notes.length}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Toplam Dosya
                </span>
                <div className="h-10 w-10 rounded-full bg-violet-100 dark:bg-violet-900 text-violet-700 dark:text-violet-200 flex items-center justify-center shadow-md">
                  <span className="text-sm font-bold">{files.length}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Toplam Teklif
                </span>
                <div className="h-10 w-10 rounded-full bg-pink-100 dark:bg-pink-900 text-pink-700 dark:text-pink-200 flex items-center justify-center shadow-md">
                  <span className="text-sm font-bold">{offers.length}</span>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* Sağ kolon - Toplantılar, notlar ve dosyalar */}
        <div className="lg:col-span-2 space-y-6">
          {/* Toplantılar */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-indigo-100 dark:border-gray-600 transition-all duration-300">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl shadow-md">
                <FiCalendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Proje Toplantıları
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {meetings.length > 0 ? `${meetings.length} toplantı planlandı` : 'Toplantı yok'}
                </p>
              </div>
            </div>

            {meetings.length > 0 ? (
              <div className="space-y-3">
                {meetings
                  .sort((a, b) => new Date(b.meeting_start_date) - new Date(a.meeting_start_date))
                  .map((meeting) => {
                    const meetingDate = new Date(meeting.meeting_start_date);
                    const isToday = meetingDate.toDateString() === new Date().toDateString();
                    const isTomorrow = meetingDate.toDateString() === new Date(Date.now() + 86400000).toDateString();
                    
                    return (
                      <div 
                        key={meeting.id} 
                        className="group relative bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-600 hover:border-indigo-200 dark:hover:border-indigo-500 transition-all duration-300 hover:shadow-md transform hover:scale-[1.02]"
                      >
                        <div className="flex items-start space-x-3">
                          {/* Sol taraf - Zaman */}
                          <div className="flex-shrink-0 text-center">
                            <div className="bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900 dark:to-purple-900 rounded-lg p-5 min-w-[110px]">
                              <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                                {formatDateOnly(meeting.meeting_start_date)}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {formatTimeOnly(meeting.meeting_start_date)}
                              </div>
                            </div>
                            
                            {/* Toplantı Lokasyonu - Sol kutunun altına */}
                            {meeting.meeting_location && (
                              <div className="flex items-center justify-center space-x-1 mt-2">
                                <FiMapPin className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-gray-500 dark:text-gray-400 text-center">
                                  {meeting.meeting_location}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Orta - İçerik */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="text-sm font-bold text-gray-900 dark:text-white">
                                {meeting.meeting_subject}
                              </h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                meeting.meeting_type === 'Online' 
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                              }`}>
                                {meeting.meeting_type}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                meeting.meeting_status === 'Tamamlandı' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                                meeting.meeting_status === 'Planlandı' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' :
                                'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                              }`}>
                                {meeting.meeting_status}
                              </span>
                            </div>
                            
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                              {meeting.meeting_description}
                            </p>
                            
                            {/* Toplantıyı Yaratan */}
                            <div className="flex items-center space-x-1 mb-2">
                              <FiUser className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Yaratan: {meeting.created_by_name || 'Belirtilmemiş'}
                              </span>
                            </div>
                            
                            {/* Katılımcılar - En alta taşındı */}
                            <div className="flex items-center space-x-1 mt-2">
                              <FiUsers className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                Katılımcılar: 
                              </span>
                              {meeting.meeting_participation ? (() => {
                                try {
                                  const participants = JSON.parse(meeting.meeting_participation);
                                  return participants.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {participants.slice(0, 3).map((participant, index) => (
                                        <span 
                                          key={index}
                                          className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-medium"
                                        >
                                          {participant.user_fullname}
                                        </span>
                                      ))}
                                      {participants.length > 3 && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          +{participants.length - 3} daha
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-red-500 dark:text-red-400">
                                      Katılımcı eklenmemiştir
                                    </span>
                                  );
                                } catch (error) {
                                  return (
                                    <span className="text-xs text-red-500 dark:text-red-400">
                                      Katılımcı eklenmemiştir
                                    </span>
                                  );
                                }
                              })() : (
                                <span className="text-xs text-red-500 dark:text-red-400">
                                  Katılımcı eklenmemiştir
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Guest kullanıcılar için toplantı işlem butonlarını gizle */}
                        {currentUser?.user_type !== 'guest' && meeting.created_by == currentUser?.id && (
                          <div className="absolute top-4 right-4 flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {meeting.meeting_status === 'Planlandı' && (
                              <button
                                onClick={() => {
                                  setEditingMeeting(meeting);
                                  setShowMeetingModal(true);
                                }}
                                className="text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-300"
                                title="Düzenle"
                              >
                                <FiEdit size={16} />
                              </button>
                            )}
                            <button
                              onClick={() => handleStatusChange(meeting)}
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                              title="Durum Değiştir"
                            >
                              <FiSettings size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteMeeting(meeting.id)}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              title="Sil"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-full flex items-center justify-center">
                      <FiCalendar className="w-8 h-8 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400 font-medium">
                        Henüz toplantı eklenmemiş
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        Yeni toplantılar eklendiğinde burada görünecek
                      </p>
                    </div>
                  </div>
                </div>
              )}
          </div>

          {/* Proje Notları */}
          <div className="card p-0 overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Proje Notları
                </h3>
              </div>
            </div>
            <div className="p-6">
              {notes.length > 0 ? (
                <div className="space-y-3">
                  {notes.map((note) => (
                    <div
                      key={note.id}
                      className="flex items-start justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                              <FiFileText className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {note.note_type}
                              </p>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(note.created_at).toLocaleDateString('tr-TR')}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                              {note.project_notes}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {note.created_by}
                            </p>
                          </div>
                        </div>
                      </div>
                      {currentUser?.user_type !== 'guest' && (
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={() => {
                              setEditingNote(note);
                              setShowNoteModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                            title="Düzenle"
                          >
                            <FiEdit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteNote(note.id)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            title="Sil"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FiFileText className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    Henüz not eklenmemiş
                  </p>
                </div>
              )}
            </div>
          </div>

        {/* Dosyalar */}
          <div className="card p-0 overflow-hidden">
            <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Dosyalar</h3>
            </div>
            <div className="p-6">
            {/* Sürükle-bırak yükleme alanı */}
            {/* Guest kullanıcılar için dosya yükleme alanını gizle */}
            {currentUser?.user_type !== 'guest' && (
              <div
                onDragEnter={onDragOver}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                className={`border-2 border-dashed rounded-lg p-6 mb-6 transition-colors ${
                  dragActive
                    ? 'border-blue-400 bg-blue-50 dark:border-blue-500 dark:bg-gray-700'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <AiOutlineUpload className="text-blue-600 dark:text-blue-400" size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      Dosyayı buraya sürükleyip bırakın ya da tıklayın
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Maks. 20MB. Tek dosya yükleyin.
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2 sm:justify-end">
                  <input
                    type="text"
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    placeholder="Açıklama (opsiyonel)"
                    className="input-field w-full sm:w-56"
                  />
                  <label className="btn-secondary cursor-pointer whitespace-nowrap">
                    Dosya Seç
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => e.target.files && handleFiles(e.target.files)}
                    />
                  </label>
                </div>
              </div>
              {(uploading || uploadMessage) && (
                <div className="mt-3">
                  {uploading && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">Yükleniyor...</div>
                  )}
                  {uploadMessage && (
                    <div className={`mt-2 text-sm px-3 py-2 rounded-md inline-block ${
                      uploadMessageType === 'success' 
                        ? 'hidden'
                        : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-800'
                    }`}>
                      {uploadMessage}
                    </div>
                  )}
                </div>
              )}
              </div>
            )}

            {files.length > 0 ? (
              <div className="space-y-3">
                {files.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-gray-100 dark:bg-gray-600 rounded-full flex items-center justify-center">
                        <FiFileText className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {file.project_file_name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {file.project_file_description}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(file.updated_at).toLocaleDateString('tr-TR')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <a
                        href={`${API_ORIGIN}${file.project_file_path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Görüntüle/İndir"
                        download
                      >
                        <FiFileText size={16} />
                      </a>
                      {/* Guest kullanıcılar için dosya silme butonunu gizle */}
                      {currentUser?.user_type !== 'guest' && (
                        <button
                          onClick={() => {
                            setConfirmData({ type: 'file', id: file.id, title: 'Bu dosyayı silmek istiyor musunuz?' });
                            setShowConfirmModal(true);
                          }}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          title="Sil"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FiFileText className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Henüz dosya yüklenmemiş
                </p>
              </div>
            )}
            </div>
          </div>

        {/* Teklifler */}
        <div className="card p-0 overflow-hidden">
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Teklifler</h3>
            </div>
          </div>
          <div className="p-6">
          {/* Upload alanı popup'a taşındı */}

          {offers.length > 0 ? (
            <div className="space-y-3">
              {offers.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((offer) => (
                <div key={offer.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-gray-800 dark:to-gray-700 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="h-12 w-12 bg-gradient-to-br from-blue-500 to-purple-600 dark:from-blue-600 dark:to-purple-700 rounded-xl flex items-center justify-center shadow-lg">
                      <FiFileText className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        Teklif #{offer.id}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {offer.offer_file_description}
                      </p>
                          <div className="mt-1">
                            <span className={getOfferStatusClass(offer.offer_status)}>
                              {offer.offer_status}
                            </span>
                            
                            {/* Tüm durumlarda detaylı bilgileri göster */}
                            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-600 dark:text-gray-300">
                              <span className="inline-flex items-center rounded-md bg-white/50 dark:bg-gray-800/50 px-2 py-0.5">
                                <span className="mr-1 opacity-70">Tutar:</span>
                                <strong>
                                  {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: (offer.currency || 'TRY') === 'USD' ? 'USD' : 'TRY' }).format(Number(offer.offer_total || 0))}
                                </strong>
                              </span>
                              <span className="inline-flex items-center rounded-md bg-white/50 dark:bg-gray-800/50 px-2 py-0.5">
                                <span className="mr-1 opacity-70">Kârlılık:</span>
                                <strong>
                                  {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: (offer.currency || 'TRY') === 'USD' ? 'USD' : 'TRY' }).format(Number(offer.profitability || 0))}
                                </strong>
                              </span>
                              {Number(offer.offer_total) > 0 && (
                                <span className="inline-flex items-center rounded-md bg-white/50 dark:bg-gray-800/50 px-2 py-0.5">
                                  <span className="mr-1 opacity-70">Kârlılık Oranı:</span>
                                  <strong>
                                    {((Number(offer.profitability || 0) / Number(offer.offer_total)) * 100).toFixed(2)}%
                                  </strong>
                                </span>
                              )}
                              {offer.currency && (
                                <span className="inline-flex items-center rounded-md bg-white/50 dark:bg-gray-800/50 px-2 py-0.5">
                                  <span className="mr-1 opacity-70">Para Birimi:</span>
                                  <strong>{offer.currency}</strong>
                                </span>
                              )}
                              {offer.created_at && (
                                <span className="inline-flex items-center rounded-md bg-white/50 dark:bg-gray-800/50 px-2 py-0.5">
                                  <span className="mr-1 opacity-70">Gönderilme:</span>
                                  <strong>{(() => {
                                    // Veritabanından gelen tarihi direkt format et
                                    const dateStr = offer.created_at;
                                    if (dateStr.includes('T')) {
                                      // ISO format ise
                                      const [date, time] = dateStr.split('T');
                                      const [year, month, day] = date.split('-');
                                      const [hour, minute] = time.split(':');
                                      return `${day}.${month}.${year} ${hour}:${minute}`;
                                    } else {
                                      // MySQL DATETIME format ise
                                      const [date, time] = dateStr.split(' ');
                                      const [year, month, day] = date.split('-');
                                      const [hour, minute] = time.split(':');
                                      return `${day}.${month}.${year} ${hour}:${minute}`;
                                    }
                                  })()}</strong>
                                </span>
                              )}
                              {/* Dosya durumu */}
                              {(!offer.offer_file_path || offer.offer_file_path.trim() === '') ? (
                                <span className="inline-flex items-center rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-2 py-0.5 text-amber-600 dark:text-amber-400">
                                  <FiFileText className="mr-1 h-3 w-3" />
                                  <span className="opacity-70">Dosya eklenmemiştir</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-2 py-0.5 text-green-600 dark:text-green-400">
                                  <button
                                    onClick={async () => {
                                      try {
                                        const response = await projectService.downloadProjectOfferFile(offer.id);
                                        // Dosya indirme işlemi
                                        const url = window.URL.createObjectURL(new Blob([response.data]));
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.setAttribute('download', offer.offer_file_name || 'teklif.pdf');
                                        document.body.appendChild(link);
                                        link.click();
                                        link.remove();
                                        window.URL.revokeObjectURL(url);
                                      } catch (error) {
                                        toast.error('Dosya indirilemedi');
                                      }
                                    }}
                                    className="flex items-center hover:bg-green-200 dark:hover:bg-green-800 rounded px-1 py-0.5 mr-1"
                                    title="Dosyayı indir"
                                  >
                                    <FiFileText className="mr-1 h-3 w-3" />
                                    <span className="opacity-70">{offer.offer_file_name || 'Dosya'}</span>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteFile(offer)}
                                    className="hover:bg-red-200 dark:hover:bg-red-800 rounded-full p-0.5"
                                    title="Dosyayı sil"
                                  >
                                    <FiX className="h-3 w-3" />
                                  </button>
                                </span>
                              )}
                            </div>
                          </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {/* Guest kullanıcılar için teklif işlem butonlarını gizle */}
                    {currentUser?.user_type !== 'guest' && (
                      <>
                        <button
                          onClick={() => {
                            setEditingOffer(offer);
                            setOfferDescription(offer.offer_file_description || '');
                            setOfferTotal(offer.offer_total || '');
                            setOfferProfitability(offer.profitability || '');
                            setOfferCurrency(offer.currency || 'TRY');
                            setOfferStatus(offer.offer_status || 'Hazırlandı');
                            setOfferDate(offer.created_at ? offer.created_at.split('T')[0] : '');
                            setOfferTime(offer.created_at ? offer.created_at.split('T')[1]?.substring(0, 5) : '');
                            setShowOfferModal(true);
                          }}
                          className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                          title="Düzenle"
                        >
                          <FiEdit size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setOfferStatusModal({ visible: true, offerId: offer.id, status: offer.offer_status });
                          }}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Durum Değiştir"
                        >
                          <FiSettings size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setConfirmData({ type: 'offer', id: offer.id, title: 'Bu teklifi silmek istiyor musunuz?' });
                            setShowConfirmModal(true);
                          }}
                          className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          title="Sil"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FiFileText className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Henüz teklif yüklenmemiş
              </p>
            </div>
          )}
          </div>
        </div>
        </div>
      </div>

      {/* Toplantı modal'ı */}
      {showMeetingModal && (
        <ProjectMeetingModal
          projectId={id}
          meeting={editingMeeting}
          onClose={() => {
            setShowMeetingModal(false);
            setEditingMeeting(null);
          }}
          onSuccess={() => {
            setShowMeetingModal(false);
            setEditingMeeting(null);
            queryClient.invalidateQueries(['project', id]);
          }}
        />
      )}

      {/* Not modal'ı */}
      {showNoteModal && (
        <ProjectNoteModal
          projectId={id}
          note={editingNote}
          onClose={() => {
            setShowNoteModal(false);
            setEditingNote(null);
          }}
          onSuccess={() => {
            setShowNoteModal(false);
            setEditingNote(null);
            queryClient.invalidateQueries(['project', id]);
          }}
        />
      )}

      {/* Durum değiştirme modal'ı */}
      {showStatusModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="modal-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">
                  Toplantı Durumu Değiştir
                </h3>
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="text-white hover:text-gray-300"
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Yeni Durum
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="input-field"
                >
                  <option value="Planlandı">Planlandı</option>
                  <option value="Tamamlandı">Tamamlandı</option>
                  <option value="İptal">İptal</option>
                  <option value="Ertelendi">Ertelendi</option>
                </select>
              </div>

              {(newStatus === 'İptal' || newStatus === 'Ertelendi') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Açıklama <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={statusDescription}
                    onChange={(e) => setStatusDescription(e.target.value)}
                    className="input-field"
                    rows={3}
                    placeholder="Durum değişikliği için açıklama giriniz..."
                    required
                  />
                </div>
              )}

              <div className="flex items-center justify-end space-x-3 pt-6">
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="btn-secondary"
                  disabled={updateMeetingStatusMutation.isLoading}
                >
                  İptal
                </button>
                <button
                  onClick={handleUpdateStatus}
                  className="btn-primary"
                  disabled={updateMeetingStatusMutation.isLoading}
                >
                  {updateMeetingStatusMutation.isLoading ? 'Güncelleniyor...' : 'Güncelle'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bilgi Modal'ı (Upload sonucu) */}
      {showInfoModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="modal-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Bilgi</h3>
                <button onClick={() => setShowInfoModal(false)} className="text-white hover:text-gray-300">
                  <FiX size={20} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {uploadMessage || (uploadMessageType === 'success' ? 'İşlem başarılı.' : 'İşlem tamamlandı.')}
              </p>
              <div className="flex items-center justify-end space-x-3 pt-6">
                <button onClick={() => setShowInfoModal(false)} className="btn-primary">Tamam</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Silme Onay Modal'ı */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-modal">
            <div className="modal-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Silme Onayı</h3>
                <button onClick={() => setShowConfirmModal(false)} className="text-white hover:text-gray-300">
                  <FiX size={20} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700 dark:text-gray-300">{confirmData.title || 'Bu öğeyi silmek istiyor musunuz?'}</p>
              <div className="flex items-center justify-end space-x-3 pt-6">
                <button onClick={() => setShowConfirmModal(false)} className="btn-secondary">İptal</button>
                <button onClick={handleConfirmDelete} className="btn-danger">Evet, Sil</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Teklif Modal */}
      {showOfferModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-lg">
            <div className="modal-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">{editingOffer ? 'Teklifi Düzenle' : 'Yeni Teklif'}</h3>
                <button onClick={() => setShowOfferModal(false)} className="text-white hover:text-gray-300">
                  <FiX size={20} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Durum</label>
                  <select
                    className="input-field w-full"
                    value={offerStatus}
                    onChange={(e) => setOfferStatus(e.target.value)}
                  >
                    <option>Hazırlandı</option>
                    <option>Gönderildi</option>
                    <option>Müşteri Onay Sürecinde</option>
                    <option>Müşteri Revize İstedi</option>
                    <option>Teklif Kabul Edilmedi</option>
                    <option>Teklif Onaylandı</option>
                    <option>Revize Gönderilecek</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Para Birimi</label>
                  <select
                    className="input-field w-full"
                    value={offerCurrency}
                    onChange={(e) => setOfferCurrency(e.target.value)}
                  >
                    <option value="TL">TL</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Teklif Tutarı</label>
                  <CurrencyInput
                    id="offerTotal"
                    name="offerTotal"
                    placeholder="0.00"
                    value={offerTotal}
                    onValueChange={(value, name) => {
                      // CurrencyInput değeri temiz format (100000.50) olarak gelir
                      setOfferTotal(value || '');
                    }}
                    prefix=""
                    suffix=""
                    decimalsLimit={2}
                    decimalSeparator="."
                    groupSeparator=","
                    allowDecimals={true}
                    allowNegativeValue={false}
                    className="input-field w-full"
                    style={{
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      lineHeight: 'inherit'
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kârlılık Tutarı</label>
                  <CurrencyInput
                    id="offerProfitability"
                    name="offerProfitability"
                    placeholder="0.00"
                    value={offerProfitability}
                    onValueChange={(value, name) => {
                      // CurrencyInput değeri temiz format (100000.50) olarak gelir
                      setOfferProfitability(value || '');
                    }}
                    prefix=""
                    suffix=""
                    decimalsLimit={2}
                    decimalSeparator="."
                    groupSeparator=","
                    allowDecimals={true}
                    allowNegativeValue={false}
                    className="input-field w-full"
                    style={{
                      fontFamily: 'inherit',
                      fontSize: 'inherit',
                      lineHeight: 'inherit'
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gönderilme Tarihi</label>
                  <input
                    type="date"
                    value={offerDate}
                    onChange={(e) => setOfferDate(e.target.value)}
                    className="input-field w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gönderilme Saati</label>
                  <input
                    type="time"
                    value={offerTime}
                    onChange={(e) => setOfferTime(e.target.value)}
                    className="input-field w-full"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Açıklama</label>
                  <input
                    type="text"
                    value={offerDescription}
                    onChange={(e) => setOfferDescription(e.target.value)}
                    placeholder="Teklif açıklaması (opsiyonel)"
                    className="input-field w-full"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Dosya (opsiyonel)</label>
                  <label className="btn-secondary cursor-pointer inline-flex items-center">
                    <AiOutlineUpload className="mr-2" /> Dosya Seç
                    <input
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        if (!e.target.files) return;
                        setSelectedOfferFile(e.target.files[0]);
                      }}
                    />
                  </label>
                  {selectedOfferFile && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">Seçilen: {selectedOfferFile.name}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 pt-6">
                <button
                  onClick={() => setShowOfferModal(false)}
                  className="btn-secondary"
                  disabled={uploading}
                >
                  İptal
                </button>
                <button
                  onClick={editingOffer ? handleUpdateOffer : async () => {
                    try {
                      setUploading(true);
                      // CurrencyInput değerleri zaten temiz format (1000000.50)
                      // Timezone offset hesapla (Türkiye UTC+3)
                      const timezoneOffset = new Date().getTimezoneOffset();
                      const turkeyOffset = 180; // UTC+3 = 180 dakika
                      const offsetDiff = timezoneOffset + turkeyOffset;
                      
    const combinedDateTime = `${offerDate}T${offerTime}:00+03:00`;
                      await projectService.uploadOffer(id, selectedOfferFile || null, { 
                        description: offerDescription || uploadDescription, 
                        status: offerStatus, 
                        total: parseFloat(offerTotal) || 0, 
                        profitability: parseFloat(offerProfitability) || 0, 
                        currency: offerCurrency,
                        created_at: combinedDateTime,
                        offer_type: 'project' // Görev 2: Proje teklifi için 'project'
                      });
                      setShowOfferModal(false);
                      setUploadMessage('');
                      setUploadMessageType(null);
                      toast.success('Teklif başarıyla oluşturuldu');
                      setUploadDescription('');
                      setOfferDescription('');
                      setOfferTotal('');
                      setOfferProfitability('');
                      setOfferCurrency('TL');
                      setOfferDate(new Date().toISOString().slice(0, 10));
                      setOfferTime(new Date().toISOString().slice(11, 16));
                      setSelectedOfferFile(null);
                      queryClient.invalidateQueries(['project', id]);
                    } catch (error) {
                      const msg = error.response?.data?.message || 'Teklif kaydedilemedi';
                      toast.error(msg);
                      setUploadMessage(msg);
                      setUploadMessageType('error');
                    } finally {
                      setUploading(false);
                    }
                  }}
                  className="btn-primary"
                  disabled={uploading}
                >
                  {uploading ? 'Kaydediliyor...' : (editingOffer ? 'Güncelle' : 'Gönder')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proje düzenleme modal'ı */}
      {showProjectModal && (
        <ProjectModal
          project={editingProject}
          onClose={() => {
            setShowProjectModal(false);
            setEditingProject(null);
          }}
          onSuccess={() => {
            setShowProjectModal(false);
            setEditingProject(null);
            queryClient.invalidateQueries(['project', id]);
          }}
        />
      )}

      {/* Offer Durum Değiştir Modal */}
      {offerStatusModal?.visible && (
        <div className="modal-overlay">
          <div className="modal-content delete-modal">
            <div className="modal-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Teklif Durumu Değiştir</h3>
                <button onClick={() => setOfferStatusModal({ visible: false, offerId: null, status: 'Hazırlandı' })} className="text-white hover:text-gray-300">
                  <FiX size={20} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Yeni Durum</label>
                <select
                  className="input-field"
                  value={offerStatusModal.status}
                  onChange={(e) => setOfferStatusModal(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option>Hazırlandı</option>
                  <option>Gönderildi</option>
                  <option>Müşteri Onay Sürecinde</option>
                  <option>Müşteri Revize İstedi</option>
                  <option>Teklif Kabul Edilmedi</option>
                  <option>Teklif Onaylandı</option>
                  <option>Revize Gönderilecek</option>
                </select>
              </div>
              <div className="flex items-center justify-end space-x-3 pt-6">
                <button
                  onClick={() => setOfferStatusModal({ visible: false, offerId: null, status: 'Hazırlandı' })}
                  className="btn-secondary"
                >
                  İptal
                </button>
                <button
                  onClick={async () => {
                    try {
                      await projectService.updateOfferStatus(offerStatusModal.offerId, offerStatusModal.status);
                      toast.success('Teklif durumu güncellendi');
                      setOfferStatusModal({ visible: false, offerId: null, status: 'Hazırlandı' });
                      queryClient.invalidateQueries(['project', id]);
                    } catch (error) {
                      toast.error(error.response?.data?.message || 'Durum güncellenemedi');
                    }
                  }}
                  className="btn-primary"
                >
                  Güncelle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dosya Silme Onay Modal'ı */}
      {showDeleteFileModal && fileToDelete && (
        <DeleteConfirmModal
          isOpen={showDeleteFileModal}
          title="Dosyayı Sil"
          message={`"${fileToDelete.offer_file_name || 'Bu dosya'}" dosyasını silmek istediğinizden emin misiniz?`}
          onConfirm={confirmDeleteFile}
          onCancel={() => {
            setShowDeleteFileModal(false);
            setFileToDelete(null);
          }}
          isLoading={false}
        />
      )}
    </div>
  );
};

export default ProjectDetail;

